/**
 * Spinner — an indeterminate busy indicator: a Font Awesome glyph spun by
 * `fa-spin`, FA's own animation (its `prefers-reduced-motion` rule stops the
 * spin — `animation: none` — and ships with the kit, so the atom adds none of
 * its own).
 *
 * Figma: Atom/Spinner. The panel's only axis is `size=[sm|md|lg]`, and sm is
 * its default, so this atom's default is sm too. `xl` (64px) is not a Figma
 * frame: it is the page and section loader size the legacy Loader and the
 * apps' GlobalLoader drew, kept so those can move to this atom.
 *
 * No Base UI part and no hooks, so the atom renders as a Server Component.
 * `role="status"` with an `aria-label` gives the element an accessible name a
 * screen reader can announce; a caller that already labels the busy region can
 * pass its own. A spinner placed inside another live region passes
 * `role="presentation"` instead — two nested status regions announce the same
 * event twice — and loses the name with it, since a presentational element
 * carrying `aria-label` has its role ignored and lands back in the tree.
 *
 * Stylesheet note, since the SCSS carries no comments: `--spinner-size` is
 * declared once on the block with the sm value — Atlas exports no size scale,
 * so 16/24/32 are the Figma frames written as a component hook, and a size
 * modifier repoints the value only. The declarations sit in `:where()`, so
 * they carry no specificity: a caller needing a one-off size sets
 * `--spinner-size` on its own `className` and wins regardless of load order. Figma stacks a navy `sweep` over a full
 * Surface/Selected `track` ring; the glyph is the sweep, and the track is an
 * inset box-shadow on the round block, `0.0625em` thick because that is the
 * spinner-third arc's stroke (1/16 em, outer edge at the em box), so the ring
 * sits exactly under the arc at every size.
 */

import {type FC} from 'react';
import cn from 'classnames';

import {sizeClass} from '../../shared/classNames';
import {Icon} from '../Icon';

import type {SpinnerProps} from './types';

import styles from './styles.module.scss';

const Spinner: FC<SpinnerProps> = ({
  className,
  size = 'sm',
  role = 'status',
  'aria-label': ariaLabel = 'Loading',
  ...props
}) => (
  <span
    {...props}
    role={role}
    aria-label={role === 'status' ? ariaLabel : undefined}
    data-slot="spinner"
    data-size={size}
    className={cn(
      styles.spinner,
      sizeClass(styles, 'spinner', size),
      className,
    )}>
    <Icon name="fa-light fa-spinner-third fa-spin" />
  </span>
);

export {Spinner};
